import React from 'react';
import { CheckCircle, Clock, CreditCard, Calendar } from 'lucide-react';
import Icon from '../../../components/AppIcon';


/**
 * RefundProgressTracker Component
 * Displays refund progress as pending→processing→completed with payment method and expected arrival date
 */
const RefundProgressTracker = ({ refundStatus, paymentMethod, expectedArrivalDate, refundAmount }) => {
  // Define progress steps
  const steps = [
    { id: 'pending', label: 'Beklemede', icon: Clock, color: 'text-yellow-600' },
    { id: 'processing', label: 'İşleniyor', icon: CreditCard, color: 'text-blue-600' },
    { id: 'completed', label: 'Tamamlandı', icon: CheckCircle, color: 'text-green-600' }
  ];

  // Get current step index
  const getCurrentStepIndex = () => {
    return steps?.findIndex(step => step?.id === refundStatus);
  };

  const currentStepIndex = getCurrentStepIndex();

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date?.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  // Get status message
  const getStatusMessage = () => {
    switch (refundStatus) {
      case 'pending':
        return 'İade talebiniz inceleniyor';
      case 'processing':
        return 'İadeniz işleme alındı ve yakında hesabınıza yansıyacak';
      case 'completed':
        return 'İadeniz başarıyla tamamlandı';
      default:
        return '';
    }
  };

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">İade Durumu</h3>
          <p className="text-sm text-gray-600 mt-1">{getStatusMessage()}</p>
        </div>
        {refundAmount && (
          <div className="text-right">
            <p className="text-sm text-gray-600">İade Tutarı</p>
            <p className="text-2xl font-bold text-indigo-600">₺{refundAmount?.toFixed(2)}</p>
          </div>
        )}
      </div>
      {/* Progress Steps */}
      <div className="relative">
        {/* Progress Line */}
        <div className="absolute top-6 left-0 w-full h-1 bg-gray-200">
          <div
            className="h-full bg-gradient-to-r from-yellow-500 via-blue-500 to-green-500 transition-all duration-500"
            style={{
              width: `${((currentStepIndex + 1) / steps?.length) * 100}%`
            }}
          />
        </div>

        {/* Steps */}
        <div className="relative flex justify-between">
          {steps?.map((step, index) => {
            const Icon = step?.icon;
            const isCompleted = index <= currentStepIndex;
            const isCurrent = index === currentStepIndex;

            return (
              <div key={step?.id} className="flex flex-col items-center">
                {/* Step Circle */}
                <div
                  className={`
                    relative z-10 w-12 h-12 rounded-full flex items-center justify-center
                    transition-all duration-300 transform
                    ${isCompleted ? 'bg-white border-4 scale-110' : 'bg-gray-200 border-4 border-gray-300'}
                    ${isCurrent ? 'shadow-lg ring-4 ring-opacity-50' : ''}
                    ${isCompleted && step?.id === 'pending' ? 'border-yellow-500 ring-yellow-200' : ''}
                    ${isCompleted && step?.id === 'processing' ? 'border-blue-500 ring-blue-200' : ''}
                    ${isCompleted && step?.id === 'completed' ? 'border-green-500 ring-green-200' : ''}
                  `}
                >
                  <Icon
                    className={`w-6 h-6 ${isCompleted ? step?.color : 'text-gray-400'}`}
                  />
                </div>
                {/* Step Label */}
                <div className="mt-3 text-center">
                  <p className={`text-sm font-medium ${isCompleted ? 'text-gray-900' : 'text-gray-500'}`}>
                    {step?.label}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Additional Information */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Payment Method */}
        {paymentMethod && (
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center space-x-3">
              <CreditCard className="w-5 h-5 text-gray-600" />
              <div>
                <p className="text-xs text-gray-500">Ödeme Yöntemi</p>
                <p className="text-sm font-semibold text-gray-900">{paymentMethod}</p>
              </div>
            </div>
          </div>
        )}

        {/* Expected Arrival Date */}
        {expectedArrivalDate && refundStatus !== 'completed' && (
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center space-x-3">
              <Calendar className="w-5 h-5 text-gray-600" />
              <div>
                <p className="text-xs text-gray-500">Tahmini Varış Tarihi</p>
                <p className="text-sm font-semibold text-gray-900">{formatDate(expectedArrivalDate)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Completion Date */}
        {refundStatus === 'completed' && (
          <div className="bg-white rounded-lg p-4 border border-green-100 bg-green-50">
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-xs text-green-600">İade Tamamlandı</p>
                <p className="text-sm font-semibold text-green-900">
                  İade tutarı hesabınıza yansıdı
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Help Text */}
      {refundStatus === 'processing' && (
        <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            💡 <strong>Bilgi:</strong> İade işlemi genellikle 5-7 iş günü içinde tamamlanır. 
            Ödeme yönteminize bağlı olarak süre değişiklik gösterebilir.
          </p>
        </div>
      )}
    </div>
  );
};

export default RefundProgressTracker;